import type { ReactNode } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import { useExportRoutesModal } from '@/components/modals/ExportRoutesModal'
import { type ExportOrderSource, useExportOrderModal } from '@/components/modals/ExportOrderModal'
import { useSupportTicketModal } from '@/components/modals/SupportTicketModal'
import { SupportTicket } from '../SupportTicketModal/types'

interface Props {
  children: ReactNode
}

interface Context {
  exportRoutes: {
    show: () => void
    close: () => void
  }
  exportOrders: {
    show: (source: ExportOrderSource) => void
    close: () => void
  }
  supportTicket: {
    show: (ticket?: SupportTicket) => void
    close: () => void
  }
}

const noop = () => undefined

function getInitialContext(): Context {
  return {
    exportRoutes: {
      show: noop,
      close: noop,
    },
    exportOrders: {
      show: noop,
      close: noop,
    },
    supportTicket: {
      show: noop,
      close: noop,
    },
  }
}

const context = createContext<Context>(getInitialContext())

export function ResidentModals(props: Props) {
  const { children } = props

  const [exportOrderSource, setExportOrderSource] = useState<ExportOrderSource>('default')
  const [supportTicket, setSupportTicket] = useState<SupportTicket>()

  const {
    show: showExportRoutesModal,
    Modal: ExportRoutesModal,
    close: closeExportRoutesModal,
  } = useExportRoutesModal()

  const {
    show: showExportOrdersModal,
    Modal: ExportOrdersModal,
    close: closeExportOrdersModal,
  } = useExportOrderModal()

  const {
    show: showSupportTicketModal,
    Modal: SupportTicketModal,
    close: closeSupportTicketModal,
  } = useSupportTicketModal()

  const handleShowExportOrders = (source: ExportOrderSource) => {
    setExportOrderSource(source)
    showExportOrdersModal()
  }

  const handleCloseExportOrders = () => {
    setExportOrderSource('default')
    closeExportOrdersModal()
  }

  const handleSupportTicket = (ticket?: SupportTicket) => {
    setSupportTicket(ticket)
    showSupportTicketModal()
  }

  const contextValue = useMemo<Context>(() => {
    return {
      exportRoutes: {
        show: showExportRoutesModal,
        close: closeExportRoutesModal,
      },
      exportOrders: {
        show: handleShowExportOrders,
        close: handleCloseExportOrders,
      },
      supportTicket: {
        show: handleSupportTicket,
        close: closeSupportTicketModal,
        modal: SupportTicketModal,
      },
    }
  }, [
    showExportRoutesModal,
    closeExportRoutesModal,
    showExportOrdersModal,
    closeExportOrdersModal,
    handleSupportTicket,
    closeSupportTicketModal,
  ])

  return (
    <context.Provider value={contextValue}>
      {children}
      <ExportRoutesModal />
      <ExportOrdersModal source={exportOrderSource} />
      <SupportTicketModal ticket={supportTicket} />
    </context.Provider>
  )
}

export function useResidentModalsApi() {
  return useContext(context)
}
